import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, StateCard, Badge, tableStyle, thStyle, tdStyle, fmtMoney } from './ProcurementShared';

export default function ProcurementOverview() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/procurement/overview`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed');
      setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [companyId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (initialLoading || !data) return <div style={{ padding: 32, textAlign: 'center' }}>Loading...</div>;

  const c = data.counts || {};

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>🛒 Procurement Overview</h2>
      </div>

      <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e', marginBottom: 10 }}>Pending Actions</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
        <StateCard label="PR Drafts" count={c.pr_draft} color="#9ca3af" icon="📝" />
        <StateCard label="PR Awaiting Approval" count={c.pr_pending_approval} color="#f59e0b" icon="✅" />
        <StateCard label="PO Drafts" count={c.po_draft} color="#9ca3af" icon="🧾" />
        <StateCard label="PO Awaiting Approval" count={c.po_pending_approval} color="#f59e0b" icon="✅" />
        <StateCard label="PO Confirmed" count={c.po_confirmed} color="#3b82f6" icon="📦" />
        <StateCard label="PO Partially Received" count={c.po_partially_received} color="#f59e0b" icon="🚚" />
        <StateCard label="Suppliers Incomplete" count={c.suppliers_incomplete} color="#f59e0b" icon="🏢" />
        <StateCard label="Receivings (Draft)" count={c.receivings_draft} color="#9ca3af" icon="📥" />
        <StateCard label="Returns (Draft)" count={c.returns_draft} color="#9ca3af" icon="↩️" />
        <StateCard label="Invoice Differences" count={c.supplier_invoice_differences} color="#ef4444" icon="⚠️" />
        <StateCard label="Payables Overdue" count={c.payables_overdue} color="#ef4444" icon="⏰" />
        <StateCard label="Payables Due Soon" count={c.payables_due_soon} color="#f59e0b" icon="💰" />
        <StateCard label="Deliveries Due Soon" count={c.deliveries_due_soon} color="#f59e0b" icon="🚛" />
        <StateCard label="Deliveries Overdue" count={c.deliveries_overdue} color="#ef4444" icon="🚨" />
      </div>

      {(data.custom_status_cards || []).length > 0 && (
        <>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e', marginBottom: 10 }}>Custom PO Statuses</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
            {data.custom_status_cards.map(s => (
              <StateCard key={s.code} label={s.name} count={s.count} color="#8b5cf6" icon="🔖" />
            ))}
          </div>
        </>
      )}

      <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e', marginBottom: 10 }}>Recent Purchase Orders</h3>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', marginBottom: 24 }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['PO #', 'Supplier', 'Total', 'Status', 'Date'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {(data.recent_purchase_orders || []).length === 0 && (
                <tr><td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No purchase orders yet</td></tr>
              )}
              {(data.recent_purchase_orders || []).map(po => (
                <tr key={po.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{po.po_number}</td>
                  <td style={tdStyle}>{po.supplier?.name || '—'}</td>
                  <td style={tdStyle}>{fmtMoney(po.total)}</td>
                  <td style={tdStyle}><Badge value={po.status} /></td>
                  <td style={tdStyle}>{po.created_at ? new Date(po.created_at).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e', marginBottom: 10 }}>Recent Purchase Requests</h3>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['PR #', 'Priority', 'Est. Cost', 'Status', 'Date'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {(data.recent_purchase_requests || []).length === 0 && (
                <tr><td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No purchase requests yet</td></tr>
              )}
              {(data.recent_purchase_requests || []).map(pr => (
                <tr key={pr.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{pr.pr_number}</td>
                  <td style={tdStyle}>{pr.priority}</td>
                  <td style={tdStyle}>{fmtMoney(pr.estimated_cost)}</td>
                  <td style={tdStyle}><Badge value={pr.status} /></td>
                  <td style={tdStyle}>{pr.created_at ? new Date(pr.created_at).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
